import { useCallback, useEffect, useState } from "react";

import { CryptoSwapsService } from "../services/cryptoSwaps.service";
import type { OtcSavedWallet } from "../types/cryptoSwaps.types";

export interface UseOtcSavedWalletsResult {
  wallets: OtcSavedWallet[];
  isLoading: boolean;
  refresh: () => void;
  remove: (id: string) => Promise<void>;
}

interface Snapshot {
  organizationKey: string;
  wallets: OtcSavedWallet[];
}

/** The organization's saved destinations. A failed read leaves the list empty: typing an address still works. */
export function useOtcSavedWallets(
  organizationId: string | null,
  enabled: boolean,
): UseOtcSavedWalletsResult {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const organizationKey = organizationId ?? "";

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    CryptoSwapsService.listSavedWallets(organizationId)
      .then((wallets) => {
        if (!cancelled) setSnapshot({ organizationKey, wallets });
      })
      .catch(() => {
        if (!cancelled) setSnapshot({ organizationKey, wallets: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, organizationKey, enabled, reloadToken]);

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

  const remove = useCallback(
    async (id: string) => {
      await CryptoSwapsService.deleteSavedWallet(id, organizationId);
      setSnapshot((current) =>
        current ? { ...current, wallets: current.wallets.filter((wallet) => wallet.id !== id) } : current,
      );
    },
    [organizationId],
  );

  // A refresh keeps showing the previous list, so a selected wallet never blinks out mid-flow.
  const current = enabled && snapshot?.organizationKey === organizationKey ? snapshot.wallets : null;
  return {
    wallets: current ?? [],
    isLoading: enabled && current === null,
    refresh,
    remove,
  };
}
